import React, { useState } from 'react';
import {
    Box,
    Card,
    TextField,
    Button,
    Typography,
    IconButton,
    InputAdornment,
    Alert,
    CircularProgress,
    Stack,
    Divider
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Login as LoginIcon,
    Lock as LockIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState('');

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Get the page user was trying to access before being redirected to login
    const from = location.state?.from?.pathname || "/";

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setLocalError('Please enter both email and password');
            return;
        }

        setLoading(true);
        setLocalError('');

        try {
            const result = await login(email, password);
            if (result.success) {
                navigate(from, { replace: true });
            } else {
                setLocalError(result.message);
            }
        } catch (err) {
            setLocalError('An error occurred during login. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                height: '100vh',
                width: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 9999
            }}
        >
            {/* Background elements for depth */}
            <Box sx={{
                position: 'absolute',
                width: '500px',
                height: '500px',
                borderRadius: '50%',
                background: 'rgba(59, 130, 246, 0.1)',
                filter: 'blur(100px)',
                top: '-200px',
                right: '-100px'
            }} />
            <Box sx={{
                position: 'absolute',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                background: 'rgba(30, 64, 175, 0.15)',
                filter: 'blur(80px)',
                bottom: '-150px',
                left: '-100px'
            }} />

            <Card
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    p: 4,
                    borderRadius: '24px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'visible'
                }}
            >
                {/* Logo / Brand Section */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            bgcolor: 'primary.main',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            boxShadow: '0 8px 16px rgba(37, 99, 235, 0.3)',
                            transform: 'rotate(-5deg)'
                        }}
                    >
                        <Typography variant="h4" fontWeight={900} color="white">M</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="text.primary" gutterBottom>
                        Welcome Back
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sign in to access your dashboard
                    </Typography>
                </Box>

                {localError && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                        {localError}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            placeholder="name@company.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '12px' }
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            placeholder="••••••••"
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '12px' }
                            }}
                        />

                        <Box sx={{ textAlign: 'right' }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                Forgot password?
                            </Typography>
                        </Box>

                        <Button
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            startIcon={!loading && <LoginIcon />}
                            sx={{
                                py: 1.5,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1rem',
                                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(37, 99, 235, 0.3)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                        </Button>
                    </Stack>
                </form>

            </Card>

            {/* Footer Copyright */}
            <Typography
                variant="caption"
                sx={{
                    position: 'absolute',
                    bottom: 24,
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontWeight: 500
                }}
            >
                © 2026 MTANDT Group. All rights reserved.
            </Typography>
        </Box>
    );
};

export default LoginPage;
